import React, { useCallback, useEffect} from 'react';
import {set, get} from 'immutadot';
import isEqual from 'react-fast-compare';
import {extractCreationIds, applyDiffs, applyCreationIds, diffObjects, purgeCreationIds} from './id-diff-merge';
import {deepCopy} from "../utils";
import { diff } from 'deep-object-diff';
import { isObject } from 'core-js/core/object';

export type DiaFormRelationships = {
    [key: string]: string
};


function convertToObjectPath(path, values, prefix = '', returnValue = {
    path: '',
    exists: false,
    parent: null,
    arrayIndex: -1,
    isArray: false
}) {
    // takes (id).property and converts to [idx].property
    if (!path && path !== '0') {
        returnValue.path = prefix;
        returnValue.exists = true;
        return returnValue;
    }
    if (!values) {
        returnValue.path = prefix + '.' + path;
        returnValue.exists = true;
        return returnValue;
    }
    const parts = path.split('.');
    const currentPart = parts.shift();
    //console.log('DIAform FIND PATH for', path, 'with', prefix, values);
    if (currentPart.indexOf('(') > -1) {
        const divided = currentPart.split('(');
        const name = divided[0];
        const key = divided[1].slice(0, -1);
        let value = null;
        prefix = (prefix ? prefix + '.' + name : name);
        returnValue.parent = prefix;
        returnValue.isArray = true;
        values[name].forEach((v, idx) => {
            if (value) return;
//            console.log('diaForm FIND PATH check', v, 'against', key);
            if (v.id == key || v.creation_id == key) {
                //          console.log('DIAForm FIND PATH - recurse ', parts,prefix+'['+idx+']');
                value = convertToObjectPath(parts.join('.'), v, prefix + '[' + idx + ']', returnValue);
                value.arrayIndex = idx;
                //console.log('array search returned',value,parts.join('.'));
            }
        });
        return value ? value : returnValue;
    }

    if (parts.length < 1 || parts[0] == '') {
        return {path: prefix + '.' + currentPart};
    }
    return convertToObjectPath(parts.join('.'), values[currentPart], prefix + '.' + currentPart);
}

enum DiaFormActions {
    UPDATE,
    SUBMIT,
    INITIALIZE,
    STATUS_SET,
    VALUE_SET,
    ERRORS_SET,
    TRANSIENT_SET
}


type DiaFormInitialFunction<Type> = (state: DiaFormState<Type>, rawValues: Type) => DiaFormState<Type>;

export type DiaFormErrors = {
    [key: string]: string
}

type DiaFormState<Type,TransientType=never> = {
    status: 'ready' | 'loading' | 'validating' | 'submitting' | 'submit-failed',
    isDirty: boolean,
    isNew: boolean,
    initialValues: Type|null,
    transientValues?: TransientType,
    relationships?: DiaFormRelationships,
    dirtyList: string[],
    values: Type,
    errors?: DiaFormErrors,
}

export interface DiaFormSubmitCallbackProps<Type> {
    values: Type,
    initialValues: Type,
    hasFailed?: boolean,
    setTransient:(name:string,value:string)=>void,
    reInitialize?: (newValues: Type, rawInputValues: Type, isNew: boolean) => void
}

export type DiaFormAction<Type> =
    DiaFormActionSet<Type>
    | DiaFormActionStatus<Type>
    | DiaFormActionSubmit<Type>
    | DiaFormActionInitialize<Type>
    | DiaFormActionUpdate<Type>
    | DiaFormActionErrorsSet<Type>;

interface DiaFormActionSet<Type> {
    type: DiaFormActions.VALUE_SET|DiaFormActions.TRANSIENT_SET,
    name: string,
    value: any
}



interface DiaFormActionErrorsSet<Type> {
    type: DiaFormActions.ERRORS_SET,
    value: any
}


interface DiaFormActionStatus<Type> {
    type: DiaFormActions.STATUS_SET,
    value: 'submitting' | 'loading' | 'validating' | 'ready' | 'submit-failed'
}


interface DiaFormActionUpdate<Type> {
    type: DiaFormActions.UPDATE,
    values: Type,
    name: string
}

interface DiaFormActionSubmit<Type> {
    type: DiaFormActions.SUBMIT,
    onSubmit: (values: Type) => Type,
    validationSchema?: YupSchema
}

interface DiaFormActionInitialize<Type> {
    type: DiaFormActions.INITIALIZE,
    initializer?: DiaFormInitialFunction<Type>,
    relationships?: DiaFormRelationships,
    inputValues: Type,
    isNew?: boolean,
}

function isFunction<Type>(x: any): x is DiaFormInitialFunction<Type> {
    return typeof x === "function";
}

function extractChangeableObject(src,relations,currentPath=null) {
    // to objects 'connected', the only interesting change is to the ID of the object itself
    // so create a copy of the object without all the other properties and return that
    // - pre-processing the relationship list could make this a lot faster
    // - not fetching the lookup table values in the .values graphql query would avoid the need for this
    if(!relations) return src;
    if(Array.isArray(src)) {
        const output:any[] = [];
        src.forEach((innerSrc)=>{
            output.push(extractChangeableObject(innerSrc,relations,currentPath));
        });
        return output;
    }
    if(src && typeof src == 'object') {
        if(relations[currentPath??'']=='connect') {
            return {
                id:src.id
            };
        } else {
            const returnObject = {};
            Object.entries(src).forEach(([key,value])=>{
                returnObject[key]  = extractChangeableObject(value,relations,currentPath?currentPath+'.'+key:key);
            });
            return returnObject;
        }
    }
    return src;
}


function reducer<Type,TransientType>(state: DiaFormState<Type,TransientType>, action: DiaFormAction<Type,TransientType>): DiaFormState<Type,TransientType> {
    console.log('[DiaForm]', 'REDUCER', 'action', action, 'status', state.status);
    let newState: DiaFormState<Type,TransientType>;
    const getInitialValues = () => {
        return (action as DiaFormActionUpdate<Type>).name ? get(state.initialValues, (action as DiaFormActionUpdate<Type>).name) : state.initialValues;
    }
    const getCurrentValues = () => {
        return (action as DiaFormActionUpdate<Type>).name ? get(state.values, (action as DiaFormActionUpdate<Type>).name) : state.values;
    }
    switch (action.type) {
        case DiaFormActions.UPDATE:
            newState = state;
            // this is used to synchronize with a remote data source
            const initialValues = getInitialValues();
            const currentValues = getCurrentValues();
            let newValues: any = action.values;
// if creation_id is given for objects in the response, remove them and apply them to the initialValues and currentvalues
            const cIds = extractCreationIds(newValues);
            console.log('DIAFORM diff cIds',cIds);
            newValues = purgeCreationIds(newValues, cIds);
            console.log('DIAFORM diff cIds',cIds);
            const original = applyCreationIds(initialValues, cIds);
            const local = applyCreationIds(currentValues, cIds);

            // find all local changes that haven't been committed to the server yet
            console.log('DIAFORM diff between',original,local);
            const diffs = diffObjects(original, local);
            console.log('DIAFORM diffs',diffs);
            // apply those local changes to the values received from the server, and use as the new local state
            const newResult = applyDiffs(newValues, diffs);
            console.log('DIAFORM newresult',newResult);
            // set the server state to the value received from the server (with the creation_ids removed)
            newState = set(newState, 'initialValues' + (action.name ? '.' + action.name : ''),deepCopy(newValues));
            // set the local state to the merged values from above
            newState.status = 'ready';
            newState = set(newState, 'values' + (action.name ? '.' + action.name : ''), deepCopy(newResult));

            // performance hog - creates a copy of both initial/current values, with 'lookup' entries replaced with the ID
            // this could be done in a webworker to somewhat mitigate the performance penalty here
            newState = set(newState, 'isDirty', !isEqual(extractChangeableObject(newState.initialValues,newState.relationships), extractChangeableObject(newState.values,newState.relationships)));
            console.log('[DIAFORM] UPDATE newState is', newState,diffObjects((
                extractChangeableObject(newState.initialValues,newState.relationships),
                extractChangeableObject(newState.values,newState.relationships))),isEqual(extractChangeableObject(newState.initialValues,newState.relationships), extractChangeableObject(newState.values,newState.relationships))
            );
            return newState;
        case DiaFormActions.SUBMIT:
            newState = state;
            if (action.validationSchema) {
                newState = set(newState, 'status', 'validating');
                //console.log('[DiaForm]', 'action - run validation');
            } else {
                newState = set(newState, 'status', 'submitting');
                //                action.onSubmit({values:state.values,initialValues:state.initialValues});
            }
            return newState;
        case DiaFormActions.INITIALIZE:
            if (isFunction(action.initializer)) {
                console.log('[DIAFORM] INITIALIZER', action.inputValues);
                newState = action.initializer(state, action.inputValues);
            } else {
                console.log('[DIAFORM] INITIALIZE COPY', action.inputValues);
                newState = {
                    initialValues: deepCopy(action.inputValues),
                    isDirty: false,
                    isNew: false,
                    status: undefined,
                    relationships:action.relationships,
                    values: deepCopy(action.inputValues),
                    transientValues:action.transientValues
                };
                //newState = deepCopy(state);
//                newState.initialValues = ;
            }
            console.log('[DIAFORM]', 'INITIALIZED INTO', newState);
            //console.log('[DiaForm]', 'INITIALIZE', 'REDUCER', 'called function', newState, action.values);
            newState.isDirty = false;
            newState.status = 'ready';
            //NetworkStatus.waiting = false;
            newState.isNew = !!action.isNew;
            /*            } else {
                            //console.log('[DiaForm]', 'INITIALIZE', 'REDUCER', 'used values', action.values);
                            const values = action.values;
                            newState = {
                                initialValues: values,
                                values: JSON.parse(JSON.stringify(values)),
                                status: 'ready',
                                isDirty: false,
                                isNew: !!action.isNew
                            };
                        }*/
            console.log('[DIAFORM] INIT newState is', newState,diffObjects(newState.values,newState.initialValues));
            break;
        case DiaFormActions.STATUS_SET:
            newState = set(state, 'status', action.value);
            break;
        case DiaFormActions.VALUE_SET:
            const name = action.name ? 'values.' + action.name : 'values';
            //console.log('set',name,action.value);
            if (typeof action.value == 'function') {
                newState = set(state, name, action.value(get(state, name)));
            } else {
                newState = set(state, name, action.value);
            }
            // TODO: enable this, use web worker to manage somehow
            // > ...webworkers cannot be cancelled o_O
            //diffState(newState.initialValues,newState.values,newState.relationships);
            newState.isDirty = !isEqual(newState.initialValues, newState.values);
            break;
        case DiaFormActions.TRANSIENT_SET:
            const tName = action.name ? 'transientValues.' + action.name : 'transientValues';
            //console.log('set',name,action.value);
            if (typeof action.value == 'function') {
                newState = set(state, tName, action.value(get(state, tName)));
            } else {
                newState = set(state, tName, action.value);
                //console.log('[DiaForm] action route A', name, action.value, get(newState, name));
            }
        break;
    
        case DiaFormActions.ERRORS_SET:
            //console.log('[DiaForm] errors.set');
            newState = set(state, 'errors', action.value);
            break;
        default:
            return state;
    }
    console.log('[DIAFORMVALUE]',action,newState);
    return newState;
}

interface YupSchema {
    validate: (any, Object) => Promise<unknown>
}

interface DiaFormProps<Type,TransientType=never> {
    inputValues?: Type | null,
    isNew?: boolean,
    initializer?: (values: DiaFormState<Type>) => DiaFormState<Type>,
    transientValues?:TransientType,
    validationSchema?: YupSchema,
    relationships?:DiaFormRelationships,
    onSubmit?: (DiaFormSubmitCallbackProps) => Promise<void>,
    onFailure?: (any) => void,
    loaded: boolean,
}

export type DiaFormInitializer<Type> = (values: DiaFormState<Type>, Type) => DiaFormState<Type>;

interface DiaFormValue<Type,TransientType=never> {
    values: Type,
    initializer: DiaFormInitializer<Type>,
    initialValues: Type | DiaFormInitializer<Type> | null,
    transientValues?:TransientType,
    updateInitialValues: (values: Type, name) => void,
    hasFailed: () => void,
    setTransient: (name: string, value: any) => void,
    setValue: (name: string, value: any) => void,
    onChange: (evt) => void,
    isDirty: boolean,
    errors: any,
    handleSubmit: (evt?: any) => void,
}

const useDiaForm = <Type,TransientType>({
                              inputValues, isNew = false, initializer, validationSchema, onSubmit, onFailure,relationships,
                              loaded,transientValues
                          }: DiaFormProps<Type,TransientType>): DiaFormValue<Type,TransientType> => {
    console.log('DIAFORM re-render 0', inputValues, loaded,relationships);
    const args: DiaFormState<Type,TransientType> = {
        initialValues: null,
        transientValues:transientValues,
        status: 'loading',
        isDirty: false,
        relationships:relationships,
        isNew: isNew,
        values: inputValues
    };
    const [store, dispatch] = React.useReducer(reducer, args);
    const v: any = inputValues; // hack to avoid issues
    useEffect(() => {
        console.log('DIAFORM loaded changed', loaded, inputValues);
        if (!inputValues) return;
        if (inputValues?.id) return;
        // storing an array - merge if there's data, otherwise overwrite
        if (!store.initialValues) {
            console.log('[DIAFORM]', 'RE?-INITIALIZING')
            dispatch({
                type: DiaFormActions.INITIALIZE,
                inputValues: inputValues,
                initializer: initializer,
                relationships:relationships,
                isNew: isNew,
                transientValues:transientValues
            });
        } else {
            console.log('[DIAFORM]', 'UPDATING');
            // otherwise attempt to merge the update
            dispatch({
                type: DiaFormActions.UPDATE,
                values: inputValues,
                name: ''
            });
        }
    }, [loaded]);


    useEffect(() => {
        if (!v) return;
        console.log('DIAFORM initializing with2', inputValues,isNew);
        // storing an item
        if(isNew) {
            console.log('DIAFORM initializing 2');
            if(status!='ready') {
                dispatch({
                    type: DiaFormActions.STATUS_SET,
                    value: 'ready'
                });
            }
            if(!store.isNew) {
                console.log('DIAFORM initializing blank');
                dispatch({
                    type: DiaFormActions.INITIALIZE,
                    inputValues: inputValues,
                    initializer: initializer,
                    relationships:relationships,
                    isNew: true,
                    transientValues:transientValues
                });
            }

            return;
        }
        if (v?.id != store.initialValues?.id) {
            console.log('[DIAFORM]', 'RE?-INITIALIZING')
            dispatch({
                type: DiaFormActions.INITIALIZE,
                inputValues: inputValues,
                initializer: initializer,
                relationships:relationships,
                isNew: false,
                transientValues:transientValues
            });
        } else {
            console.log('[DIAFORM]', 'UPDATING');
            // otherwise attempt to merge the update
            dispatch({
                type: DiaFormActions.UPDATE,
                values: inputValues,
                name: ''
            });
        }
        //}
        /*switch(store.status) {
            case 'submit-failed':
                console.log('DIAFORM2 - TO READY',store.status,loaded);
                dispatch({type: DiaFormActions.STATUS_SET, value: 'ready'});
                return;
            case 'loading':
                console.log('DIAFORM2 - LOADING HANDLE',store.status,loaded,v?.id,isNew);
                if(loaded) {
                    dispatch({
                        type: DiaFormActions.INITIALIZE,
                        initializer: initializer,
                        isNew: isNew,
                    });
                }
            break;
            case 'ready':
                if (isNew && !store.isNew) {
                    console.log('DIAFORM2 - NEW CHANGE',store.status,loaded,values);
                    dispatch({
                        type: DiaFormActions.INITIALIZE,
                        initializer: initializer,
                        isNew: isNew,
                    });
                    return;
                }
                if (id !== store.values[idField]) {
                    console.log('DIAFORM2 - ID CHANGE',v,store.status,store.values[idField],id,loaded,values);
                    dispatch({
                        type: DiaFormActions.INITIALIZE,
                        initializer: initializer,
                        rawValues: rawInputValues,
                        isNew: false
                    });
                }
                break;
        }*/
    }, [inputValues, inputValues?.id,isNew]);

    useEffect(() => {
        switch (store.status) {
            case 'submit-failed':
                console.log('[DIAFORM] - TO READY', store.status, loaded);
                dispatch({type: DiaFormActions.STATUS_SET, value: 'ready'});
                return;
        }
    }, [store.status]);

    const { values, errors, isDirty, status} = store;

    const onChange = useCallback((evt) => {
        dispatch({
            type: DiaFormActions.VALUE_SET,
            value: evt.target.type === 'checkbox' ? (!!evt.target.checked) : evt.target.value,
            name: evt.target.name
        });
    }, []);
    const setValue = useCallback((name, value) => {
        dispatch({type: DiaFormActions.VALUE_SET, value: value, name: name});
    }, []);
    const setTransient = useCallback((name, value) => {
        console.log('SET TRANSIENT ',name,value);
        dispatch({type: DiaFormActions.TRANSIENT_SET, value: value, name: name});
    }, []);
    const hasFailed = useCallback(() => {
        dispatch({type: DiaFormActions.STATUS_SET, value: 'submit-failed'});
    }, []);
    const hasCompleted = useCallback((value) => {
        // not normally needed
        dispatch({type: DiaFormActions.STATUS_SET, value: 'ready'});
    }, []);

    // this action is also rarely needed (campaigns->poster bookings)
    const updateInitialValues = useCallback((values, name) => {
        console.log('[DIAFORM] update initial called');
        dispatch({type: DiaFormActions.UPDATE, values: values, name: name})
    }, []);

    // this action should never be needed
    const initialize = useCallback((rawInputValues, isNew) => {
        //console.log('[DiaForm]', 'initializing dispatch', newValues, rawInputValues);
        console.log('[DIAFORM] EXTERNAL INITIALIZE CALLED');
        dispatch({
            type: DiaFormActions.INITIALIZE,
            initializer: initializer,
            rawValues: rawInputValues,
            isNew: isNew
        });
    }, [inputValues, initializer]);

    useEffect(() => {
        if (store.status === 'validating') {
            console.log('[DiaForm]', 'validating', store.values);
            validationSchema.validate(store.values, {abortEarly: false}).then(() => {
                console.log('[DiaForm]', 'validation', store.errors);
                if (store.errors) {
                    dispatch({type: DiaFormActions.ERRORS_SET, value: null});
                    dispatch({type: DiaFormActions.STATUS_SET, value: 'submit-failed'});
                    if (onFailure) {
                        onFailure(null);
                    }
                }
                dispatch({type: DiaFormActions.STATUS_SET, value: 'submitting'});
            }).catch((errors) => {
                console.log('NOT VALIDATED',errors);
                const saveErrors = {};
                errors.errors.forEach(e => {
                    const p = e.split(' ');
                    saveErrors[p.shift()] = p.join(' ');
                });
                dispatch({type: DiaFormActions.ERRORS_SET, value: saveErrors});
                dispatch({type: DiaFormActions.STATUS_SET, value: 'submit-failed'});
                if (onFailure) {
                    onFailure(saveErrors);
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [store.status, onSubmit, onFailure]);

    const handleSubmit = useCallback((evt = null) => {
        console.log('[DiaForm]', 'handlingSubmit');
        if (evt && evt.preventDefault) {
            evt.preventDefault();
        }
        dispatch({type: DiaFormActions.SUBMIT, onSubmit: onSubmit, validationSchema: validationSchema});
    }, [onSubmit, validationSchema]);

    useEffect(() => {
        if (store.status == 'submitting') {
            console.log('[DiaForm]', 'submitting',store);
            if(onSubmit) {
                onSubmit({
                    values: store.values,
                    initialValues: store.initialValues,
                    hasFailed: hasFailed,
                    hasCompleted: hasCompleted,
                    updateInitialValues:updateInitialValues,
                    transientValues:store.transientValues,
                    setTransient:setTransient
                });
            }
        }
    }, [store.status]);
    console.log('[DIAFORM] return status',status);
    //console.log('[DiaForm]', 'returning', { status:status,loaded:loaded, values:  values, initialize, updateInitialValues, dispatch, hasFailed, setValue, onChange, isDirty, initialValues, errors, handleSubmit });
    return {
        values: values,
        initialize,
        initialValues: store.initialValues,
        transientValues: store.transientValues,
        updateInitialValues,
        dispatch,
        hasFailed,
        setValue,
        onChange,
        isDirty,
        errors,
        handleSubmit,
        status,
        setTransient
    };
};

export {useDiaForm};